import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useInterval } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbPageTitle, { IBreadcrumb } from '../../../components/IbPageTitle';
import { defaultApi } from '../../../apis';
import {
  IngestionPipelineOutput,
  IngestionSourceCommand,
  IngestionSourceGrade,
  IngestionStateModel,
  ProjectModel,
  ProjectPipelineOutput,
  ProviderModel,
  SearchPipelineOutput,
  TraceProviderCreationRequest,
  TraceProviderModel,
  TraceProviderNames,
  TraceProviderUpdatingRequest,
  TraceSettings,
  ProjectWidgetInput,
  ProjectAgentToolsOutput,
  ApiKeyScopeRefType,
  ProjectUpdatingRequest,
} from '../../../../gamma-api';
import { LARGE_PAGE_SIZE } from '../../../constants';
import { getProjectPath, getProjectsPath } from '../../../utils/pathUtils';
import { alertsSelectorAdd } from '../../../recoil';
import IbTabs from '../../../components/common/IbTabs';
import IbTabPane from '../../../components/common/IbTabPane';
import Settings from '../../../components/Settings';
import { ProjectSearchPipelineSettingsLayout } from '../../../components/Settings/ProjectSearchPipeline';
import ApiKeys from '../../../components/ApiKeys';
import IbIcon from '../../../components/common/IbIcon';
import ExportButton from '../../../components/ExportButton';
import ImportUpload from '../../../components/ImportUpload';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import { ValidationErrors } from '../../../types';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import IbButton from '../../../components/common/IbButton';
import ProjectModal from '../ProjectsPage/ProjectModal';
import ConfirmModal from '../ProjectsPage/ConfirmModal';

import { CONTENT_CLASS_NAME, MAIN_CLASS_NAME, INGESTION_STATE_REFRESH_INTERVAL } from './const';
import SourcesTab from './sources/SourcesTab';
import TestingChatTab from './TestingChatTab';
import TracingTab from './TracingTab';
import ThreadsTab from './ThreadsTab';

enum ProjectTabKey {
  Sources = 'sources',
  Chat = 'chat',
  Search = 'search',
  Ingestion = 'ingestion',
  Keys = 'keys',
  Tracing = 'tracing',
  Evaluation = 'evaluation',
  Threads = 'threads',
  Widget = 'widget',
  AgentTools = 'agent-tools',
}

const ProjectPage: React.FC = () => {
  const { t } = useTranslation();

  const { push } = useHistory();

  const { folderId, projectId } = useParams<{ folderId: string; projectId: string }>();
  const tabKey = useParams<{ tabKey?: string }>().tabKey?.toLowerCase() ?? ProjectTabKey.Sources;

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [project, setProject] = useState<ProjectModel>();
  const [tabsStateKey, setTabsStateKey] = useState('default');
  const [ingestionState, setIngestionState] = useState<IngestionStateModel>();
  const [providers, setProviders] = useState<ProviderModel[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [searchPipeline, setSearchPipeline] = useState<SearchPipelineOutput>();
  const [ingestionPipeline, setIngestionPipeline] = useState<IngestionPipelineOutput>();
  const [projectTraceProvider, setProjectTraceProvider] = useState<TraceProviderModel>();

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [projectModalVisible, setProjectModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [updateProject, setUpdateProject] = useState<ProjectModel>();
  const [deleteProject, setDeleteProject] = useState<ProjectModel>();

  const showSearchInput = tabKey === ProjectTabKey.Sources;

  const breadcrumbs: IBreadcrumb[] = [{ title: t('Projects'), path: getProjectsPath(folderId) }];

  const setTabKey = (value: string) => {
    push(getProjectPath(folderId, projectId, value));
  };

  const fetchProject = async () => {
    try {
      const response = await defaultApi.getOneProjectGeneralProjectsProjectIdGet(projectId);
      setProject(response.data);
    } catch (err) {
      addAlert({ content: t('Failed to load project'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchIngestionsState = async () => {
    try {
      const response = await defaultApi.getCurrentIngestionStateProjectIdGet(projectId, true, false);

      if ((project?.agent_tools.source_as_tool?.tools || []).length != response.data.sources.length) {
        await fetchProject();
      }

      setIngestionState(response.data);
    } catch (err) {
      addAlert({ content: t('Failed to load ingestion state'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const tryFetchProjectTraceProvider = async (project: ProjectModel) => {
    const traceProviderId = project.pipeline.trace?.trace_provider_id;
    if (!traceProviderId) return;

    try {
      const response = await defaultApi.getOneTraceProviderGeneralTraceProvidersTraceProviderIdGet(traceProviderId);
      setProjectTraceProvider(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  };

  const goToProjects = () => {
    push(getProjectsPath(folderId));
  };

  const savePipelines = async (
    searchPipeline: SearchPipelineOutput,
    ingestionPipeline: IngestionPipelineOutput
  ): Promise<boolean> => {
    if (!project) {
      return false;
    }

    try {
      const pipeline: ProjectPipelineOutput = {
        ...project.pipeline,
        search: searchPipeline,
        ingestion: ingestionPipeline,
      };

      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline,
        widget: project.widget,
        agent_tools: project.agent_tools,
      });

      setProject({
        ...project,
        pipeline,
      });

      await defaultApi.createProcessIngestionProcessesPost({
        project_id: project.project_id,
        brief_instructions: [],
        source_command: IngestionSourceCommand.Rebuild,
        source_grades: [IngestionSourceGrade.Releasing, IngestionSourceGrade.Released],
      });

      await fetchIngestionsState();

      setSearchPipeline(pipeline.search);
      setIngestionPipeline(pipeline.ingestion);

      addAlert({ content: t('Project Page: Project settings saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Project Page: Failed to apply settings to project'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);

      return false;
    }
    return true;
  };

  const saveSearchPipeline = async (searchPipeline: SearchPipelineOutput): Promise<boolean> => {
    if (!project) {
      return false;
    }

    try {
      const pipeline: ProjectPipelineOutput = {
        ...project.pipeline,
        search: searchPipeline,
      };

      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline,
        widget: project.widget,
        agent_tools: project.agent_tools,
      });

      setProject({
        ...project,
        pipeline,
      });
      setSearchPipeline(pipeline.search);
      addAlert({ content: t('Project Page: Project search settings saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Project Page: Failed to apply search pipeline to project'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);

      return false;
    }

    return true;
  };

  const saveIngestionPipeline = async (ingestionPipeline: IngestionPipelineOutput): Promise<boolean> => {
    if (!project) {
      return false;
    }

    try {
      const pipeline: ProjectPipelineOutput = {
        ...project.pipeline,
        ingestion: ingestionPipeline,
      };

      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline,
        widget: project.widget,
        agent_tools: project.agent_tools,
      });

      setProject({
        ...project,
        pipeline,
      });
      setIngestionPipeline(pipeline.ingestion);
      addAlert({ content: t('Project Page: Ingestion pipeline settings saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Project Page: Failed to apply ingestion pipeline to project'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);

      return false;
    }

    return true;
  };

  const rebuildIngestionPipeline = async (ingestionPipeline: IngestionPipelineOutput): Promise<boolean> => {
    if (!project) {
      return false;
    }

    try {
      const pipeline: ProjectPipelineOutput = {
        ...project.pipeline,
        ingestion: ingestionPipeline,
      };

      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline,
        widget: project.widget,
        agent_tools: project.agent_tools,
      });

      await defaultApi.createProcessIngestionProcessesPost({
        project_id: project.project_id,
        brief_instructions: [],
        source_command: IngestionSourceCommand.Rebuild,
        source_grades: [IngestionSourceGrade.Releasing, IngestionSourceGrade.Released],
      });

      await fetchIngestionsState();

      setProject({
        ...project,
        pipeline,
      });
      setIngestionPipeline(pipeline.ingestion);
      addAlert({ content: t('Project Page: Project ingestion started successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Project Page: Failed to start project ingestion'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);

      return false;
    }

    return true;
  };

  const saveTracingSettings = async (traceSettings?: TraceSettings, traceProvider?: TraceProviderModel) => {
    if (!project) {
      return;
    }
    if (!traceSettings && !traceProvider) {
      return;
    }

    try {
      let traceProviderId: string;
      if (projectTraceProvider) {
        await defaultApi.updateTraceProviderGeneralTraceProvidersTraceProviderIdPut(
          projectTraceProvider.trace_provider_id,
          {
            enabled: traceProvider?.enabled ?? false,
            name: traceProvider?.name ?? TraceProviderNames.Phoenix,
            settings: traceProvider?.settings ?? { api_base: '' },
            title: traceProvider?.title,
          } as TraceProviderUpdatingRequest
        );
        traceProviderId = projectTraceProvider.trace_provider_id;
      } else {
        const traceProviderCreationResponse = await defaultApi.createTraceProviderGeneralTraceProvidersPost({
          enabled: traceProvider?.enabled ?? false,
          name: traceProvider?.name ?? TraceProviderNames.Phoenix,
          settings: traceProvider?.settings ?? { api_base: '' },
          title: `${project.name} - ${traceProvider?.name} tracing`,
        } as TraceProviderCreationRequest);
        traceProviderId = traceProviderCreationResponse.data.trace_provider_id;
      }

      const pipeline: ProjectPipelineOutput = {
        ...project.pipeline,
        trace: {
          trace_provider_id: traceProviderId,
          project_name: traceSettings?.project_name,
        },
      };
      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline,
        widget: project.widget,
        agent_tools: project.agent_tools,
      });

      const newProject = {
        ...project,
        pipeline,
      } as ProjectModel;
      await tryFetchProjectTraceProvider(newProject);
      setProject(newProject);
      addAlert({ content: t('Project Page: Tracing settings saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Project Page: Failed to update tracing settings'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const saveWidgetSettings = async (settings: ProjectWidgetInput) => {
    if (!project) {
      return;
    }

    try {
      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline: project.pipeline,
        widget: settings,
        agent_tools: project.agent_tools,
      });

      const newProject = {
        ...project,
        widget: settings,
      } as ProjectModel;
      setProject(newProject);
      addAlert({ content: t('Project widget settings saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Failed to apply widget settings to project'), type: 'error' });
    }
  };

  const saveAgentTools = async (agentTools: ProjectAgentToolsOutput) => {
    if (!project) {
      return;
    }

    try {
      await defaultApi.updateProjectGeneralProjectsProjectIdPut(project.project_id, {
        name: project.name,
        pipeline: project.pipeline,
        widget: project.widget,
        agent_tools: agentTools,
      });

      const newProject = {
        ...project,
        agent_tools: agentTools,
      } as ProjectModel;
      setProject(newProject);
      addAlert({ content: t('Project Page: Agent tools settings saved successfully'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Project Page: Failed to apply agent tools settings to project'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onUpdateProjectClick = () => {
    setUpdateProject(project);
    setProjectModalVisible(true);
  };

  const onDeleteProjectClick = () => {
    setDeleteProject(project);
  };

  const onImportFileFinish = async (newProject: ProjectModel) => {
    setProject(newProject);
    setTabsStateKey(newProject.modified_on);
    setSearchPipeline(newProject.pipeline.search);
    setIngestionPipeline(newProject.pipeline.ingestion);
  };

  const onProjectModalEdit = async (
    newProject: ProjectUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!updateProject) return;

    try {
      await defaultApi.updateProjectGeneralProjectsProjectIdPut(updateProject.project_id, newProject);
      await fetchProject();
      setProjectModalVisible(false);
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: t('Project Page: Project updating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onProjectModalCancel = () => {
    setProjectModalVisible(false);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteProject) return;

    setDeleteConfirmLoading(true);
    try {
      await defaultApi.deleteOneProjectGeneralProjectsProjectIdDelete(deleteProject.project_id);
      goToProjects();
    } catch (e) {
      addAlert({
        content: t('Project Page: Project deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
    setDeleteConfirmLoading(false);
    setDeleteProject(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteProject(undefined);
  };

  const onTestingChatSearchPipelineSave = async (searchPipeline: SearchPipelineOutput) => {
    await saveSearchPipeline(searchPipeline);
  };

  const onSearchPipelineSettingsSave = async () => {
    if (!searchPipeline) {
      return;
    }

    await saveSearchPipeline(searchPipeline);
  };

  const onSearchPipelineSettingsChange = (searchPipeline: SearchPipelineOutput) => {
    setSearchPipeline(searchPipeline);
  };

  const onIngestionPipelineSettingsSave = async () => {
    if (!ingestionPipeline) {
      return;
    }

    await saveIngestionPipeline(ingestionPipeline);
  };

  const onIngestionPipelineSettingsRebuild = async () => {
    if (!ingestionPipeline) {
      return;
    }

    await rebuildIngestionPipeline(ingestionPipeline);
  };

  const onIngestionPipelineSettingsChange = (ingestionPipeline: IngestionPipelineOutput) => {
    setIngestionPipeline(ingestionPipeline);
  };

  const onTracingSave = async (traceSettings?: TraceSettings, traceProvider?: TraceProviderModel) => {
    await saveTracingSettings(traceSettings, traceProvider);
  };

  const onWidgetSettingsSave = async (settings: ProjectWidgetInput) => {
    await saveWidgetSettings(settings);
  };

  const onAgentToolsSave = async (agentTools: ProjectAgentToolsOutput) => {
    await saveAgentTools(agentTools);
  };

  const onSearchTextChange = (value: string) => setSearchText(value);

  useEffect(() => {
    async function loadProject() {
      if (!projectId) return;

      try {
        setLoading(true);
        setProvidersLoading(true);

        const projectResponse = await defaultApi.getOneProjectGeneralProjectsProjectIdGet(projectId);
        const project = projectResponse.data;
        setProject(project);
        setSearchPipeline(projectResponse.data.pipeline.search);
        setIngestionPipeline(projectResponse.data.pipeline.ingestion);

        const providersResponse = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
        setProviders(providersResponse.data.items || []);

        await tryFetchProjectTraceProvider(project);
      } catch (err) {
        addAlert({ content: t('Failed to load project'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setLoading(false);
        setProvidersLoading(false);
      }
    }
    loadProject().finally();
    fetchIngestionsState().finally();
  }, [projectId]);

  const onTabChange = (key: string) => setTabKey(key as ProjectTabKey);

  const onSourceTabSourceListChange = async () => {
    await fetchProject();
  };

  useInterval(() => {
    fetchIngestionsState().finally();
  }, INGESTION_STATE_REFRESH_INTERVAL);

  const renderTitle = () => {
    const contextMenuItems = [
      {
        icon: <IbIcon iconName="efferent-three" size={20} />,
        text: t('Project Page: Context: Export'),
        render: (child) => <ExportButton.ProjectExport projectId={projectId}>{child}</ExportButton.ProjectExport>,
      },
      {
        icon: <IbIcon iconName="afferent-three" size={20} />,
        text: t('Project Page: Context: Import'),
        render: (child) => (
          <ImportUpload.ProjectImport folderId={folderId} projectId={projectId} onFinish={onImportFileFinish}>
            {child}
          </ImportUpload.ProjectImport>
        ),
      },
      {
        icon: <IbIcon iconName="edit" size={20} />,
        onSelect: onUpdateProjectClick,
        text: t('Project Page: Context: Edit'),
      },
      {
        icon: <IbIcon iconName="delete" size={20} />,
        onSelect: onDeleteProjectClick,
        text: t('Project Page: Context: Delete'),
      },
    ] as IIbContextMenuItem[];

    const onContextMenuClose = () => setContextMenuVisible(false);
    const onMenuButtonClick = () => setContextMenuVisible(true);

    return (
      <div style={{ display: 'flex' }}>
        <span>{project?.name}</span>
        <IbContextMenu menuItems={contextMenuItems} visible={contextMenuVisible} onClose={onContextMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" size={24} />} type="icon" onClick={onMenuButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        breadcrumbs={breadcrumbs}
        loading={loading}
        showSearchInput={showSearchInput}
        title={renderTitle()}
        onDebouncedSearch={onSearchTextChange}
      />
      <IbTabs key={tabsStateKey} activeKey={tabKey} className={CONTENT_CLASS_NAME} onChange={onTabChange}>
        <IbTabPane key={ProjectTabKey.Sources} tab={t('Project tab: Sources')} tabKey={ProjectTabKey.Sources}>
          <SourcesTab
            ingestionPipeline={ingestionPipeline}
            pipelinesLoading={loading}
            projectId={projectId}
            searchPipeline={searchPipeline}
            searchText={searchText}
            onPipelinesChange={savePipelines}
            onSourceListChange={onSourceTabSourceListChange}
          />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Chat} tab={t('Project tab: Chat')} tabKey={ProjectTabKey.Chat}>
          <TestingChatTab
            project={project}
            providers={providers}
            providersLoading={providersLoading}
            onSearchPipelineSave={onTestingChatSearchPipelineSave}
          />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Search} tab={t('Project tab: Search')} tabKey={ProjectTabKey.Search}>
          <Settings.ProjectSearchPipeline
            layout={ProjectSearchPipelineSettingsLayout.PROJECT_SETTINGS}
            providers={providers}
            providersLoading={providersLoading}
            searchPipeline={searchPipeline}
            onChange={onSearchPipelineSettingsChange}
            onSave={onSearchPipelineSettingsSave}
          />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Ingestion} tab={t('Project tab: Ingestion')} tabKey={ProjectTabKey.Ingestion}>
          <Settings.ProjectIngestionPipeline
            ingestionPipeline={ingestionPipeline}
            ingestionState={ingestionState}
            providers={providers}
            providersLoading={providersLoading}
            onChange={onIngestionPipelineSettingsChange}
            onRebuild={onIngestionPipelineSettingsRebuild}
            onSave={onIngestionPipelineSettingsSave}
          />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Keys} tab={t('Project tab: Keys')} tabKey={ProjectTabKey.Keys}>
          <ApiKeys
            showCreationButton
            scopeRef={{ id: projectId, type: ApiKeyScopeRefType.Project, externalId: project?.external_id }}
          />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Tracing} tab={t('Project tab: Tracing')} tabKey={ProjectTabKey.Tracing}>
          <TracingTab project={project} projectTraceProvider={projectTraceProvider} onSave={onTracingSave} />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Widget} tab={t('Project tab: Widget')} tabKey={ProjectTabKey.Widget}>
          <Settings.ProjectWidget
            projectExternalId={project?.external_id}
            settings={project?.widget}
            onSave={onWidgetSettingsSave}
          />
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.Threads} tab={t('Project tab: Threads')} tabKey={ProjectTabKey.Threads}>
          {tabKey === ProjectTabKey.Threads && <ThreadsTab projectId={projectId} />}
        </IbTabPane>
        <IbTabPane key={ProjectTabKey.AgentTools} tab={t('Project tab: Agent tools')} tabKey={ProjectTabKey.AgentTools}>
          {project ? (
            <Settings.ProjectAgentTools
              agentTools={project.agent_tools}
              projectExternalId={project.external_id}
              sources={ingestionState?.sources}
              onSave={onAgentToolsSave}
            />
          ) : null}
        </IbTabPane>
      </IbTabs>
      <ProjectModal
        editProject={updateProject}
        visible={projectModalVisible}
        onCancel={onProjectModalCancel}
        onEdit={onProjectModalEdit}
      />
      <ConfirmModal.DeleteProject
        loading={deleteConfirmLoading}
        visible={!!deleteProject}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default ProjectPage;
